import { type ItemTemplate, RARITY_COLORS } from '@shefira/shared';

import type { CssAtlas } from '../assets/dom-atlas.js';
import { describeOnHover } from './item-hover.js';
import { addLevelBadge, paintItemIcon } from './item-icon.js';
import { uiFormat, uiNumber, uiText } from './text.js';
// The frame and its button first: bench.css refines them.
import './smith.css';
import './bench.css';

/**
 * The pieces every counter of a village shares, the merchant's and the
 * craftsmen's (smith, alchemist, smelter, runesmith): the purse by the head,
 * the headings between groups, an item's row as a small card, the stepper of
 * a count, and what a work needs, each line telling what the bag holds
 * against what it takes, in red when it falls short. Display only: every
 * window still sends its own intent, and the server judges.
 */

export function formatGold(amount: number): string {
  return uiFormat('{amount} or', { amount: uiNumber(amount) });
}

export function rarityHex(color: number): string {
  return `#${color.toString(16).padStart(6, '0')}`;
}

/** The purse: the gold coin at its own 32 px (smaller would blur it) and the amount. */
export function purseChip(): HTMLElement {
  const purse = document.createElement('p');
  purse.className = 'bench__purse';
  purse.title = uiText('Ta bourse');
  return purse;
}

export function setPurse(purse: HTMLElement, gold: number): void {
  purse.replaceChildren(coin(), formatGold(gold));
}

export function coin(): HTMLElement {
  const element = document.createElement('span');
  element.className = 'bench__coin';
  element.setAttribute('aria-hidden', 'true');
  return element;
}

/** The line under the title: a few words on the left, the purse on the right. */
export function benchHead(lead: Node | string | undefined, purse: HTMLElement): HTMLElement {
  const head = document.createElement('div');
  head.className = 'bench__head';
  if (typeof lead === 'string') {
    head.classList.add('bench__head--lead');
    const text = document.createElement('p');
    text.className = 'bench__lead';
    text.textContent = lead;
    head.append(text);
  } else if (lead !== undefined) head.append(lead);
  head.append(purse);
  return head;
}

/** A heading between the groups of a list (« Équipement », « Coût »). */
export function section(label: string): HTMLElement {
  const heading = document.createElement('h3');
  heading.className = 'bench__section';
  heading.textContent = label;
  return heading;
}

export interface ItemRowParts {
  row: HTMLElement;
  slot: HTMLElement;
  icon: HTMLElement;
  text: HTMLElement;
  meta: HTMLElement;
  actions: HTMLElement;
}

/**
 * An item as a small card: its icon in its slot, its name in its rarity's
 * colour over a line of details, and the buttons on the right (under it on a
 * narrow phone).
 */
export function itemRow(
  template: ItemTemplate | undefined,
  icons: CssAtlas | null,
  name?: string,
  /** Only read when the template is gone from the game (a retired lot). */
  templateId = template?.id ?? '',
): ItemRowParts {
  const row = document.createElement('div');
  row.className = 'bench__row';

  const slot = document.createElement('div');
  slot.className = 'slot';
  const icon = document.createElement('div');
  icon.className = `item item--${template?.rarity ?? 'common'}`;
  paintItemIcon(icon, template, icons, templateId);
  slot.append(icon);

  const text = document.createElement('div');
  text.className = 'bench__text';
  const title = document.createElement('span');
  title.className = 'bench__name';
  if (template !== undefined) title.style.color = rarityHex(RARITY_COLORS[template.rarity]);
  title.textContent = name ?? uiText(template?.name ?? templateId);
  const meta = document.createElement('span');
  meta.className = 'bench__meta';
  text.append(title, meta);

  const actions = document.createElement('div');
  actions.className = 'bench__actions';
  row.append(slot, text, actions);
  return { row, slot, icon, text, meta, actions };
}

/** The count on an item's icon (« 12 ») and its smith's level, as in the bag. */
export function markIcon(icon: HTMLElement, quantity: number, upgradeLevel: number): void {
  addLevelBadge(icon, upgradeLevel);
  if (quantity <= 1) return;
  const count = document.createElement('span');
  count.className = 'item__qty';
  count.textContent = String(quantity);
  icon.append(count);
}

/** An item's name with its smith's level (« Épée de fer +2 »). */
export function upgradedName(
  template: ItemTemplate | undefined,
  templateId: string,
  level: number,
): string {
  const name = uiText(template?.name ?? templateId);
  return level > 0 ? `${name} +${String(level)}` : name;
}

export interface FilterChip<T extends string> {
  value: T;
  label: string;
  count: number;
}

/** A row of chips to narrow a list (« Tout 12 », « Matériaux 8 »…), the chosen one lit. */
export function filterChips<T extends string>(
  chips: readonly FilterChip<T>[],
  chosen: T,
  onChoose: (value: T) => void,
): HTMLElement {
  const bar = document.createElement('div');
  bar.className = 'bench__filters';
  bar.setAttribute('role', 'group');
  bar.setAttribute('aria-label', uiText('Filtrer'));
  for (const { value, label, count } of chips) {
    const chip = document.createElement('button');
    chip.type = 'button';
    chip.className = 'bench__filter';
    chip.dataset.filter = value;
    chip.classList.toggle('bench__filter--active', value === chosen);
    chip.setAttribute('aria-pressed', String(value === chosen));
    chip.textContent = `${label} ${String(count)}`;
    chip.addEventListener('click', () => {
      onChoose(value);
    });
    bar.append(chip);
  }
  return bar;
}

export interface StepperOptions {
  count: number;
  /** The highest count allowed; the « + » stops there. */
  most: number;
  onStep: (next: number) => void;
  /** A « Max » button that jumps to `most`. */
  withMax?: boolean;
}

/** A count chosen by − n + (and « Max »). */
export function stepper({ count, most, onStep, withMax = false }: StepperOptions): HTMLElement {
  const box = document.createElement('div');
  box.className = 'bench__stepper';
  const step = (label: string, aria: string, next: number, enabled: boolean) => {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'bench__step';
    button.textContent = label;
    button.setAttribute('aria-label', aria);
    button.disabled = !enabled;
    button.addEventListener('click', () => {
      onStep(next);
    });
    return button;
  };
  const value = document.createElement('span');
  value.className = 'bench__count';
  value.textContent = String(count);
  box.append(
    step('−', uiText('Un de moins'), count - 1, count > 1),
    value,
    step('+', uiText('Un de plus'), count + 1, count < most),
  );
  if (withMax) {
    const max = step(uiText('Max'), uiText('Le plus possible'), most, count < most);
    max.classList.add('bench__step--max');
    box.append(max);
  }
  return box;
}

export interface Need {
  /** The icon at the head of the line: an item (`needIcon`) or the coin. */
  icon: HTMLElement;
  name: string;
  have: number;
  need: number;
  /** Gold counts are written with the locale's separators. */
  gold?: boolean;
}

/** What a work takes, one line each: icon, name, « have / need », red and « missing n » when short. */
export function needList(needs: readonly Need[]): HTMLElement {
  const list = document.createElement('div');
  list.className = 'bench__needs';
  for (const need of needs) list.append(needRow(need));
  return list;
}

function needRow({ icon, name, have, need, gold = false }: Need): HTMLElement {
  const short = have < need;
  const row = document.createElement('div');
  row.className = short ? 'bench__need bench__need--short' : 'bench__need';
  const cell = document.createElement('div');
  cell.className = 'bench__need-icon';
  cell.append(icon);
  const text = document.createElement('div');
  text.className = 'bench__text';
  const label = document.createElement('span');
  label.className = 'bench__name';
  label.textContent = name;
  text.append(label);
  if (short) {
    const missing = document.createElement('span');
    missing.className = 'bench__meta bench__meta--short';
    missing.textContent = gold
      ? uiFormat('Il manque {gold}', { gold: formatGold(need - have) })
      : uiFormat('Il en manque {count}', { count: uiNumber(need - have) });
    text.append(missing);
  }
  const count = document.createElement('span');
  count.className = 'bench__tally';
  count.title = uiText('Tu as / il faut');
  const held = document.createElement('b');
  held.textContent = uiNumber(have);
  count.append(held, ` / ${uiNumber(need)}`);
  row.append(cell, text, count);
  return row;
}

/** An item's icon for a need line, its card on hover. */
export function needIcon(
  template: ItemTemplate | undefined,
  templateId: string,
  icons: CssAtlas | null,
): HTMLElement {
  const icon = document.createElement('div');
  icon.className = `item item--${template?.rarity ?? 'common'}`;
  paintItemIcon(icon, template, icons, templateId);
  describeOnHover(icon, template);
  return icon;
}

/** The gold line of a work's needs. */
export function goldNeed(cost: number, purse: number): Need {
  return { icon: coin(), name: uiText('Or'), have: purse, need: cost, gold: true };
}

/**
 * The main button of a craftsman's window and, under it, why it waits (the
 * first thing missing), in red. The button keeps `smith__go`: the camp's
 * coach points at it.
 */
export function workAction(
  label: string,
  why: string | undefined,
  onClick: (button: HTMLButtonElement) => void,
): HTMLElement {
  const box = document.createElement('div');
  box.className = 'bench__work';
  const button = document.createElement('button');
  button.type = 'button';
  button.className = 'smith__go bench__go';
  button.textContent = label;
  button.disabled = why !== undefined;
  button.addEventListener('click', () => {
    if (button.disabled) return;
    onClick(button);
  });
  box.append(button);
  if (why !== undefined && why !== '') {
    const reason = document.createElement('p');
    reason.className = 'bench__why';
    reason.textContent = why;
    box.append(reason);
  }
  return box;
}

/** The first need that falls short, said plainly; undefined when all are met. */
export function firstShortfall(needs: readonly Need[]): string | undefined {
  const short = needs.find((need) => need.have < need.need);
  if (short === undefined) return undefined;
  return short.gold === true
    ? uiFormat('Il te manque {gold}.', { gold: formatGold(short.need - short.have) })
    : uiFormat('Il te manque {count} × {name}.', {
        count: uiNumber(short.need - short.have),
        name: short.name,
      });
}
